// src/PurchaseCredits.js
import React, { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { supabase } from "./supabaseClient";

const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  (typeof window !== "undefined" &&
    window.__ENV &&
    window.__ENV.STRIPE_PUBLISHABLE_KEY);

if (!STRIPE_PUBLISHABLE_KEY) {
  throw new Error(
    "Missing Stripe publishable key. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env.local " +
    "or window.__ENV.STRIPE_PUBLISHABLE_KEY in public/index.html."
  );
}

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Pricing (per-month display)
const STARTER_MONTHLY_PRICE = 19;
const STARTER_ANNUAL_PRICE = 15; // per month, billed annually

const STUDIO_MONTHLY_PRICE = 49;
const STUDIO_ANNUAL_PRICE = 39; // per month, billed annually

// Stripe price IDs (NEW STRIPE PROJECT — replace these)
const PRICE_ID_STARTER_MONTHLY = "price_1Sg6E6DiXooDlkmNTDdhk6Ts";
const PRICE_ID_STARTER_ANNUAL = "price_1Sg6GVDiXooDlkmNweBNydo7";
const PRICE_ID_STUDIO_MONTHLY = "price_1Sg6EdDiXooDlkmNxPyfSFL3";
const PRICE_ID_STUDIO_ANNUAL = "price_1Sg6GsDiXooDlkmNxZic2uoI";

// Contact URL for custom plans
const CONTACT_URL = "mailto:playsetapp@gmail.com?subject=Playset%20higher%20limits";

const PurchaseCredits = () => {
  const [loadingStarter, setLoadingStarter] = useState(false);
  const [loadingStudio, setLoadingStudio] = useState(false);

  const anyLoading = loadingStarter || loadingStudio;

  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const isAnnual = billingPeriod === "annual";

  const starterPrice = isAnnual ? STARTER_ANNUAL_PRICE : STARTER_MONTHLY_PRICE;
  const studioPrice = isAnnual ? STUDIO_ANNUAL_PRICE : STUDIO_MONTHLY_PRICE;

  const starterPriceId = isAnnual ? PRICE_ID_STARTER_ANNUAL : PRICE_ID_STARTER_MONTHLY;
  const studioPriceId = isAnnual ? PRICE_ID_STUDIO_ANNUAL : PRICE_ID_STUDIO_MONTHLY;

  const setLoadingForPrice = (priceId, on) => {
    if (priceId === PRICE_ID_STARTER_MONTHLY || priceId === PRICE_ID_STARTER_ANNUAL) {
      setLoadingStarter(on);
    } else if (priceId === PRICE_ID_STUDIO_MONTHLY || priceId === PRICE_ID_STUDIO_ANNUAL) {
      setLoadingStudio(on);
    }
  };

  const handleBuyPlan = async (priceId) => {
    setLoadingForPrice(priceId, true);

    console.log("Buying priceId:", priceId);


    try {
      const { data: sessionRes } = await supabase.auth.getSession();
      if (!sessionRes?.session) {
        // Adjust to your auth route
        window.location.href = "/login";
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          priceId,
          successUrl: `${window.location.origin}/dashboard`,
          cancelUrl: `${window.location.origin}/dashboard`,
        },
      });

      if (error) {
        console.error("create-checkout-session error:", error);
        if (error.context) {
          console.error("function response:", await error.context.text());
        }
        alert(error.message || "Could not start checkout.");
        return;
      }

      const sessionId = data?.sessionId;
      if (!sessionId) {
        alert("No sessionId returned.");
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) {
        alert("Stripe failed to load.");
        return;
      }

      const { error: stripeErr } = await stripe.redirectToCheckout({ sessionId });
      if (stripeErr) {
        console.error("Stripe redirectToCheckout error:", stripeErr);
        alert(stripeErr.message || "Stripe redirect failed.");
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Checkout error.");
    } finally {
      setLoadingForPrice(priceId, false);
    }
  };

  const toggleBilling = () => setBillingPeriod(isAnnual ? "monthly" : "annual");

  return (
    <div className="ml-0 md:ml-0 flex-1 flex flex-col justify-start text-[#eee] bg-[#111] relative overflow-visible">
      {/* Grid background */}
      <div
        className="pointer-events-none absolute inset-0 -mt-8"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px",
          opacity: 1,
          zIndex: 0,
          maskImage: "radial-gradient(circle at center, white, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at center, white, transparent 75%)",
        }}
        aria-hidden="true"
      />

      <div className="w-full max-w-[1000px] relative z-10 space-y-8">
        <header>
          <h2 className="font-bebas text-4xl md:text-5xl tracking-wider mb-2">GET PLAYTEST TIME</h2>
          <p className="font-nunito text-base font-semibold text-[#eee]">
            Choose a monthly plan for real players to test your game.
          </p>
          <p className="font-nunito text-base text-[#aaa]">
            Each plan gives you a pool of playtest hours metered by the actual minutes people are in-game —
            spread across unlimited testers and sessions, with video recordings and session data kept while your
            subscription is active.
          </p>
        </header>

        <div className="overflow-visible -mx-6">
          <table className="w-full table-fixed border-separate border-spacing-x-6 border-spacing-y-0">
            <colgroup>
              <col className="w-1/3" />
              <col className="w-1/3" />
              <col className="w-1/3" />
            </colgroup>

            <thead>
              <tr>
                <td className="align-top bg-[#161616] border border-b-0 border-[#2a2a2a] rounded-t-2xl p-5 pb-2">
                  <p className="text-3xl font-bebas tracking-wider mb-1">STARTER PACK</p>
                  <p className="text-sm text-[#aaa]">Ideal for early prototypes, jam games, or a small beta group.</p>
                </td>

                <td className="relative align-top bg-[#161616] border border-b-0 border-yellow-500 rounded-t-2xl p-5 pb-2">
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-[0.65rem] md:text-xs uppercase font-semibold px-6 py-1 rounded-full text-[#111] shadow inline-flex items-center justify-center whitespace-nowrap"
                  >
                    Recommended choice
                  </div>
                  <p className="text-3xl font-bebas tracking-wider mb-1">STUDIO PACK</p>
                  <p className="text-sm text-[#aaa]">Perfect for ongoing betas, Steam demos, or frequent update cycles.</p>
                </td>

                <td className="align-top bg-[#161616] border border-b-0 border-[#2a2a2a] rounded-t-2xl p-5 pb-2">
                  <p className="text-3xl font-bebas tracking-wider mb-1">CUSTOM PLAN</p>
                  <p className="text-sm text-[#aaa]">Bigger team? Multiple projects? We’ll design a plan that fits.</p>
                </td>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td className="align-top bg-[#161616] border-x border-[#2a2a2a] p-5 pt-0 pb-4">
                  <div className="flex items-baseline gap-2 mt-2">
                    <p className="text-3xl font-bebas tracking-wider">${starterPrice}</p>
                    <span className="text-xs font-nunito text-[#aaa]">
                      per month{isAnnual && ", billed annually"}
                    </span>
                  </div>
                </td>

                <td className="align-top bg-[#161616] border-x border-yellow-500 p-5 pt-0 pb-4">
                  <div className="flex items-baseline gap-2 mt-2">
                    <p className="text-3xl font-bebas tracking-wider">${studioPrice}</p>
                    <span className="text-xs font-nunito text-[#aaa]">
                      per month{isAnnual && ", billed annually"}
                    </span>
                  </div>
                </td>

                <td className="align-top bg-[#161616] border-x border-[#2a2a2a] p-5 pt-0 pb-4" />
              </tr>

              <tr>
                <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5 pt-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={toggleBilling}
                        className="relative w-12 h-6 rounded-full bg-[#262626] flex items-center px-1"
                        aria-label="Toggle Starter billing period"
                        disabled={anyLoading}
                      >
                        <span
                          className={`inline-block h-4 w-4 rounded-full bg-white transform transition-transform duration-200 ${isAnnual ? "translate-x-6" : ""
                            }`}
                        />
                      </button>
                      <span className={`text-xs font-nunito ${isAnnual ? "text-white" : "text-[#777]"}`}>Annual</span>
                    </div>
                    <span className={`text-xs font-nunito text-green-400 transition ${isAnnual ? "opacity-100" : "opacity-0"}`}>
                      Save $48
                    </span>
                  </div>
                </td>

                <td className="bg-[#161616] border border-yellow-500 border-b-[#2a2a2a] border-t-0 p-5 pt-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={toggleBilling}
                        className="relative w-12 h-6 rounded-full bg-[#262626] flex items-center px-1"
                        aria-label="Toggle Studio billing period"
                        disabled={anyLoading}
                      >
                        <span
                          className={`inline-block h-4 w-4 rounded-full bg-white transform transition-transform duration-200 ${isAnnual ? "translate-x-6" : ""
                            }`}
                        />
                      </button>
                      <span className={`text-xs font-nunito ${isAnnual ? "text-white" : "text-[#777]"}`}>Annual</span>
                    </div>
                    <span className={`text-xs font-nunito text-green-400 transition ${isAnnual ? "opacity-100" : "opacity-0"}`}>
                      Save $120
                    </span>
                  </div>
                </td>

                <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5 pt-0" />
              </tr>

              <tr>
                <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5">
                  <button
                    onClick={() => handleBuyPlan(starterPriceId)}
                    disabled={anyLoading}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-yellow-500 text-black px-4 py-2 text-base font-nunito font-semibold disabled:opacity-60"
                  >
                    {loadingStarter ? "Loading…" : "Choose Starter"}
                  </button>
                </td>

                <td className="bg-[#161616] border border-yellow-500 border-b-[#2a2a2a] border-t-0 p-5">
                  <button
                    onClick={() => handleBuyPlan(studioPriceId)}
                    disabled={anyLoading}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-yellow-500 text-black px-4 py-2 text-base font-nunito font-semibold disabled:opacity-60"
                  >
                    {loadingStudio ? "Loading…" : "Choose Studio"}
                  </button>
                </td>

                <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5">
                  <button
                    onClick={() => (window.location.href = CONTACT_URL)}
                    disabled={anyLoading}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-[#7c3aed] px-4 py-2 text-base font-nunito font-semibold disabled:opacity-60"
                  >
                    Talk to us
                  </button>
                </td>
              </tr>

              <tr>
                <td className="align-top bg-[#161616] border border-[#2a2a2a] border-t-0 rounded-b-2xl p-5">
                  <ul className="space-y-2 text-sm text-[#aaa] pricing-bullets">
                    <li>10 hours of playtesting each month</li>
                    <li>1 game, up to 2GB</li>
                    <li>Metered by actual minutes played</li>
                    <li>Any number of testers & sessions</li>
                    <li>Full recordings & feedback timeline</li>
                  </ul>
                </td>

                <td className="align-top bg-[#161616] border border-yellow-500 border-t-0 rounded-b-2xl p-5">
                  <ul className="space-y-2 text-sm text-[#aaa] pricing-bullets">
                    <li>50 hours of playtesting each month</li>
                    <li>Up to 3 games, 3GB each</li>
                    <li>Metered by actual minutes played</li>
                    <li>Any number of testers & sessions</li>
                    <li>Full recordings & feedback timeline</li>
                  </ul>
                </td>

                <td className="align-top bg-[#161616] border border-[#2a2a2a] border-t-0 rounded-b-2xl p-5">
                  <ul className="space-y-2 text-sm text-[#aaa] pricing-bullets">
                    <li>More games & storage</li>
                    <li>Volume discounts</li>
                    <li>Longer recording retention</li>
                    <li>Ideal for studios, publishers & labs</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCredits;
